import { z } from 'zod';
import { useZodForm } from '@/components/form/use-zod-form';
import { fractionToPercentInput } from '@/lib/money';
import { percentInput } from '@/lib/schemas';
import { type FiscalProfile, type TaxRegime, REGIME_LABELS, useUpdateProfile } from './api/fiscal';

type ProfileInput = Omit<FiscalProfile, 'validFrom'>;

/**
 * Perfil da tela → o que vai para o servidor. Fora do Simples não há anexo nem cálculo pelo
 * faturamento: a alíquota informada é obrigatória. No Simples com cálculo automático, ela é só a
 * reserva enquanto não há 12 meses de faturamento.
 */
export const profileSchema = z
  .object({
    regime: z.enum(Object.keys(REGIME_LABELS) as [TaxRegime, ...TaxRegime[]]),
    annex: z.enum(['I', 'II']),
    calculated: z.boolean(),
    manual: percentInput({ required: false }),
  })
  .superRefine((value, context) => {
    const useCalculated = value.regime === 'SIMPLES_NACIONAL' && value.calculated;
    if (!useCalculated && value.manual === null) {
      context.addIssue({
        code: 'custom',
        path: ['manual'],
        message: 'Informe o percentual efetivo.',
      });
    }
  })
  .transform((value): ProfileInput => ({
    regime: value.regime,
    simplesAnnex: value.regime === 'SIMPLES_NACIONAL' ? value.annex : null,
    rateMode: value.regime === 'SIMPLES_NACIONAL' && value.calculated ? 'CALCULATED' : 'MANUAL',
    manualRate: value.manual,
  }));

/** Fração da API ("0.065") para o campo em percentual ("6,5"). */
/**
 * O perfil fiscal como está **na tela**, compartilhado entre o formulário e a importação da
 * planilha. Importar o faturamento sem salvar o perfil não calculava nada — o servidor só calcula
 * com perfil salvo —, e quem importa quer ver a alíquota, não descobrir um segundo botão. Então a
 * importação salva o perfil da tela quando ele ainda não existe ou mudou (`saveIfNeeded`).
 *
 * Recebe o perfil já carregado (nulo = nunca salvo): o formulário nasce dele, sem copiar o dado do
 * servidor para o estado por efeito. Quem chama recria o hook quando o perfil salvo muda.
 */
export function useProfileDraft(saved: FiscalProfile | null) {
  const update = useUpdateProfile();
  const form = useZodForm(profileSchema, {
    regime: saved?.regime ?? 'SIMPLES_NACIONAL',
    annex: saved?.simplesAnnex ?? 'I',
    calculated: saved ? saved.rateMode === 'CALCULATED' : true,
    manual: fractionToPercentInput(saved?.manualRate ?? null),
  });
  const [regime, calculated] = form.watch(['regime', 'calculated']);
  const isSimples = regime === 'SIMPLES_NACIONAL';

  function differsFromSaved(input: ProfileInput): boolean {
    if (!saved) return true;
    return (
      saved.regime !== input.regime ||
      saved.simplesAnnex !== input.simplesAnnex ||
      saved.rateMode !== input.rateMode ||
      (saved.manualRate === null ? null : Number(saved.manualRate)) !==
        (input.manualRate === null ? null : Number(input.manualRate))
    );
  }

  async function save(input: ProfileInput): Promise<void> {
    await update.mutateAsync(input);
  }

  /**
   * Depois da importação: salva o perfil da tela se ele ainda não existe ou mudou.
   * `'saved'` = salvou agora; `'unchanged'` = já estava salvo igual; `{ missing }` = falta dado.
   */
  async function saveIfNeeded(): Promise<'saved' | 'unchanged' | { missing: string }> {
    const result = profileSchema.safeParse(form.getValues());
    if (!result.success) {
      return { missing: result.error.issues[0]?.message ?? 'Complete o perfil fiscal.' };
    }
    if (!differsFromSaved(result.data)) return 'unchanged';
    await save(result.data);
    return 'saved';
  }

  return {
    form,
    isSimples,
    useCalculated: isSimples && calculated,
    isSaving: update.isPending,
    saveError: update.error,
    save,
    saveIfNeeded,
  };
}

export type ProfileDraft = ReturnType<typeof useProfileDraft>;
